import { useMutation, useQueryClient } from "@tanstack/react-query";
import useWorkspace from "./useWorkspace";
import { useAtomValue } from "jotai";
import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import WorkspaceAPI from "../../api/WorkspaceAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import { newToastEvent } from "../../../utils/ToastUtil";

/**
 * This hook has the option to update a workpsace at Cloud and Workspace Level.
 *
 * Cloud Level: Allows to set a workspace as `Default` or `Show/Hide`.
 * Workspace: Updates `Name`, `Filters`, `Prefills` and so on.
 * @param cloudLevel
 */
const useUpdateWorkspaceSettings = (cloudLevel = false) => {
  const { wPermissions } = useWorkspacePermissions();
  const { cPermissions } = useCloudPermissions();
  const queryClient = useQueryClient();
  const { workspace } = useWorkspace();
  const tenantID = useAtomValue(tenantIDAtom);

  const updateSettings = useMutation({
    mutationFn: ({
      workspaceID,
      payload,
      onSuccess,
    }: {
      workspaceID: string;
      payload: any;
      onSuccess?: () => void;
    }) => {
      if (!wPermissions.workspace_modify_workspace_settings && !cloudLevel) {
        return Promise.reject(
          "User does not have permission to modify this workspace's settings"
        );
      }

      if (cloudLevel && !cPermissions.cloud_modify_workspace) {
        return Promise.reject(
          "User does not have permission to modify this workspace"
        );
      }

      return WorkspaceAPI.updateWorkspaceSettings(
        workspaceID,
        payload,
        cloudLevel
      ).then((data) => {
        const updatedWorkspace = data.data.data.workspace;

        if (workspace.id === updatedWorkspace.id) {
          queryClient.setQueryData<any>(
            ["workspace", workspace.id],
            (data: any) => ({
              ...data,
              workspace: updatedWorkspace,
            })
          );
        }

        queryClient.invalidateQueries({
          queryKey: ["workspaces", tenantID],
        });
        callFunctionIfExists(onSuccess);
      });
    },
    onError: (error: any) => {
      newToastEvent("error", error.response.data.message);
    },
  });

  return {
    updateSettings: updateSettings.mutate,
    updateSubmitting: updateSettings.isPending,
    updateError: updateSettings.error
      ? getErrorMessage(updateSettings.error)
      : null,
  };
};

export default useUpdateWorkspaceSettings;
